import { Contribution } from './comparisonUtils';

/**
 * How the second dataset differs from the first (the baseline).
 *
 * A single nullable number can't express this: "no data", "no change" and "grew from zero" are
 * different answers that would all have collapsed into one dash. The percentage is signed, and
 * divided by |a| so the sign always describes the direction of movement even when the baseline is
 * negative — which happens for real, e.g. avoided burden in consequential system models.
 */
export type ImpactChange = { kind: 'value'; percent: number } | { kind: 'none' } | { kind: 'from-zero' };

export const computeChange = (a?: number, b?: number): ImpactChange => {
  if (a === undefined || b === undefined) return { kind: 'none' };
  if (a === b) return { kind: 'value', percent: 0 };
  if (a === 0) return { kind: 'from-zero' };
  return { kind: 'value', percent: ((b - a) / Math.abs(a)) * 100 };
};

/** Display form of an ImpactChange, so the row component and any tests agree on the wording. */
export const formatChange = (change: ImpactChange): string => {
  switch (change.kind) {
    case 'value':
      if (change.percent === 0) return '0%';
      return `${change.percent > 0 ? '+' : '−'}${Math.round(Math.abs(change.percent))}%`;
    case 'from-zero':
      return 'from 0';
    default:
      return '—';
  }
};

/**
 * Contributor ramp, **lightest first**: the largest contributor is the lightest green and the
 * smallest the darkest, matching the legend's highest → lowest reading order.
 *
 * The two modes differ in span, not merely in tint — a dark background swallows green.800, so dark
 * mode compresses the ramp to 300–600 while light mode uses the full 200–800 spread. Components pick
 * one with `useColorModeValue`; this module stays free of React so it remains testable.
 *
 * The **last entry is reserved for the rolled-up "other" segment**, so the ramp's length also fixes
 * how many individual contributors get named — four colours means top 3 plus "other". Changing the
 * ramp length changes the breakdown; see `buildSegments`.
 */
export const CONTRIBUTOR_COLORS_LIGHT = ['green.200', 'green.400', 'green.600', 'green.800'];
export const CONTRIBUTOR_COLORS_DARK = ['green.300', 'green.400', 'green.500', 'green.600'];

/**
 * Styling for a bar whose impact score is real but which has no contributor breakdown. Teal rather
 * than green precisely so it sits *outside* the contributor ramp: green.400 is a ramp colour in both
 * modes, so a no-breakdown bar used to be indistinguishable from a second-ranked contributor. The
 * diagonal hatch reinforces it. Shared with the legend swatch so the two can't drift apart.
 */
export const NO_BREAKDOWN_BG = { light: 'teal.400', dark: 'teal.500' };
export const NO_BREAKDOWN_HATCH = { light: 'rgba(255, 255, 255, 0.45)', dark: 'rgba(255, 255, 255, 0.25)' };

export const hatchBackgroundImage = (stripeColor: string) =>
  `repeating-linear-gradient(135deg, ${stripeColor} 0 4px, transparent 4px 8px)`;

export type ContributionSegment = Contribution & { color: string };

/**
 * Ranks contributions by magnitude, keeps the top few, and rolls the remainder into an "other"
 * segment.
 *
 * Magnitude, not signed value: a share can be negative (avoided burden in consequential system
 * models), and a large negative contributor is a major part of the result, not the least important
 * one. Sorting on the raw value buried it at the bottom and swept it into "other".
 *
 * `colors` is injected rather than read from the module so the ramp can follow the colour mode while
 * this stays a pure function. Its length sets how many contributors are named: the final colour is
 * reserved for "other", so every segment gets a distinct shade and none of them collide.
 */
export const buildSegments = (contributions: Contribution[], colors: string[]): ContributionSegment[] => {
  const maxSegments = Math.max(colors.length - 1, 1);
  const sorted = [...contributions].sort((x, y) => Math.abs(y.share) - Math.abs(x.share));
  const top = sorted.slice(0, maxSegments);
  const rest = sorted.slice(maxSegments);

  const segments: ContributionSegment[] = top.map((contribution, index) => ({
    ...contribution,
    color: colors[Math.min(index, colors.length - 1)],
  }));

  if (rest.length) {
    segments.push({
      key: 'other',
      label: 'other',
      share: rest.reduce((sum, contribution) => sum + contribution.share, 0),
      impact: rest.reduce((sum, contribution) => sum + contribution.impact, 0),
      color: colors[colors.length - 1],
    });
  }

  return segments;
};
